import * as React from 'react';

import { Box, Typography } from '@mui/material';
import { styled, useThemeProps } from '@mui/material/styles';

import Locale from '../Locale';
import PropTypes from 'prop-types';

export const MuiThemeName = 'OsanoHeader';

const HeaderRoot = styled(Box, {
    name: MuiThemeName,
    slot: 'root',
})(({ theme, ownerState }) => ({
    margin: theme.spacing(0, 0, 2),
    padding: 0,
    ...(!!ownerState.divider && {
        paddingBottom: theme.spacing(2),
        borderBottom: `${typeof ownerState.divider === 'string' ? ownerState.divider : `${ownerState.divider}px`} solid ${theme.palette.divider}`,
    }),
    '&:empty': {
        display: 'none',
    },
}));

const HeaderTitle = styled(Typography, {
    name: MuiThemeName,
    slot: 'title',
})(({ theme, ownerState }) => ({
    color: theme.palette.text.primary,
    margin: 0,
    overflowWrap: 'anywhere',
    ...(ownerState.hasSubtitle && {
        marginBottom: theme.spacing(0.5),
    }),
    ...(ownerState.hasIcon && {
        alignItems: 'center',
        display: 'flex',
        gap: theme.spacing(1),
    }),
}));

const HeaderSubtitle = styled(Typography, {
    name: MuiThemeName,
    slot: 'subtitle',
})(({ theme, ownerState }) => ({
    color: theme.palette.text.secondary,
    margin: 0,
    overflowWrap: 'anywhere',
    ...(ownerState.hasSubtitle && {
        marginTop: theme.spacing(0.5),
    }),
}));

/**
 * @typedef HeaderProps
 * @property {string} [title] The title to display in the header.
 * @property {string} [subtitle] The subtitle to display below the title.
 * @property {string} [variant='h1'] The variant of the title typography.
 * @property {number|string} [divider=1] The width of the divider line below the header. Can be a number (in pixels) or a string (e.g., '1em').
 * @property {React.ReactNode|string} [icon] An optional icon to display in the header. This can be any valid React node, such as an SVG or an icon component, or even a simple emoji.
 * @property {import('../Locale/Locale.component').LocaleProps['options']} [translationOptions] Options for translating the title and subtitle.
 * @property {object} [slotProps] Props to pass to the slots.
 * @property {object} [slotProps.title] Props to pass to the title element.
 * @property {object} [slotProps.subtitle] Props to pass to the subtitle element.
 */

/**
 * Header component for displaying a title and optional subtitle.
 * Theme-aware and supports custom styling via the theme name `OsanoHeader`.
 */
const Header =
    /** @type {React.ForwardRefExoticComponent<import('@mui/material').BoxProps & HeaderProps>} */ (
        React.forwardRef(function OsanoHeader({ children, ...inProps }, ref) {
            // The `useThemeProps` hook allows us to hook into the default props for the component defined in our theme.
            const props = useThemeProps({ props: inProps, name: MuiThemeName });
            const {
                icon,
                title,
                subtitle,
                variant,
                divider,
                translationOptions,
                slotProps,
                ...remain
            } = props;
            const ownerState = React.useMemo(
                () => ({ divider, hasIcon: !!icon, hasTitle: !!title, hasSubtitle: !!subtitle }),
                [divider, icon, title, subtitle]
            );

            return (
                <HeaderRoot ref={ref} ownerState={ownerState} {...remain}>
                    {title && (
                        <HeaderTitle
                            ownerState={ownerState}
                            variant={
                                variant ||
                                'h2' /* Default to h2 because it's the most common use case */
                            }
                            {...slotProps?.title}
                        >
                            {icon && (
                                <Box component="span" role="img">
                                    {icon}
                                </Box>
                            )}
                            {React.isValidElement(title) ? (
                                title
                            ) : (
                                <Locale path={title} options={translationOptions} />
                            )}
                        </HeaderTitle>
                    )}
                    {subtitle && (
                        <HeaderSubtitle
                            ownerState={ownerState}
                            variant="body1"
                            {...slotProps?.subtitle}
                        >
                            {React.isValidElement(subtitle) ? (
                                subtitle
                            ) : (
                                <Locale path={subtitle} options={translationOptions} />
                            )}
                        </HeaderSubtitle>
                    )}
                    {children}
                </HeaderRoot>
            );
        })
    );

Header.propTypes = {
    children: PropTypes.node,
    divider: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    icon: PropTypes.node,
    slotProps: PropTypes.shape({
        root: PropTypes.object,
        subtitle: PropTypes.object,
        title: PropTypes.object,
    }),
    subtitle: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    translationOptions: PropTypes.object,
    variant: PropTypes.string,
};

export default Header;
